import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";
import { usePasswordStatus } from "../hooks/usePasswordStatus";
import { useOTPFlow } from "../hooks/useOTPFlow";
import { usePasswordUpdate } from "../hooks/usePasswordUpdate";
import { useProfileFields } from "../hooks/useProfileFields";
import { useProfileForm } from "../hooks/useProfileForm";
import InlineAlert from "./UI/DesignStandards/Notifications/InlineAlert";
import EmailDisplay from "./profile/EmailDisplay";
import NameSection from "./profile/NameSection";
import PhoneSection from "./profile/PhoneSection";
import UsbcIdSection from "./profile/UsbcIdSection";
import PasswordSection from "./profile/PasswordSection";
import SupportSection from "./profile/SupportSection";

const ProfileForm = () => {
  const { user } = useAuth();
  const { profile, updateProfile } = useProfile();
  const { hasPassword, checkPasswordStatus } = usePasswordStatus();
  const {
    requireOTP,
    otpCodeSent,
    otpCode,
    otpCodeValid,
    sendingOTP,
    otpError,
    sendOTP,
    handleOTPCodeChange,
    resendOTP,
    resetOTPFlow,
    resetCodeSent,
    startOTPFlow,
    setOtpError,
  } = useOTPFlow(user?.email);

  const {
    formData,
    passwordChecks,
    showPassword,
    showConfirmPassword,
    handleChange,
    resetPasswordFields,
    resetField,
    setShowPassword,
    setShowConfirmPassword,
  } = useProfileForm(profile);

  const {
    loading: fieldsLoading,
    editingName,
    editingPhone,
    editingUsbcId,
    errors,
    setEditingName,
    setEditingPhone,
    setEditingUsbcId,
    handleNameUpdate,
    handlePhoneUpdate,
    handleUsbcIdUpdate,
    cancelEdit: cancelFieldEdit,
    setErrors,
  } = useProfileFields({ profile, updateProfile });

  const [editingPassword, setEditingPassword] = useState(false);
  const [error, setError] = useState(null);

  const { loading: passwordLoading, updatePassword } = usePasswordUpdate({
    hasPassword,
    otpCode,
    otpCodeValid,
    setOtpError,
    setHasPassword: () => {
      // Trigger a refresh of password status
      checkPasswordStatus();
    },
    onSuccess: () => {
      setEditingPassword(false);
      resetOTPFlow();
      resetPasswordFields();
    },
    onCodeExpired: () => {
      // Reset code sent state to allow requesting new code (matches original behavior)
      resetCodeSent();
    },
  });

  // Handle password update with proper error handling
  const handlePasswordUpdate = async () => {
    const success = await updatePassword(formData, setErrors);
    if (!success) {
      // Error already set by updatePassword
      return;
    }
  };

  // Handle password edit start
  const handlePasswordEdit = async () => {
    setEditingPassword(true);
    await checkPasswordStatus();
    startOTPFlow();
  };

  // Handle password edit cancel
  const handlePasswordCancel = () => {
    setEditingPassword(false);
    resetOTPFlow();
    resetPasswordFields();
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.currentPassword;
      delete newErrors.newPassword;
      delete newErrors.confirmPassword;
      return newErrors;
    });
  };

  // Handle field cancel with reset
  const cancelEdit = (section) => {
    if (section === "password") {
      handlePasswordCancel();
    } else {
      cancelFieldEdit(section, () => resetField(section));
    }
    // Clear all errors when cancelling (matches original behavior)
    setErrors({});
  };

  // Wrapper for handleChange that includes setErrors
  const handleFormChange = (e) => {
    handleChange(e, setErrors);
  };

  return (
    <div className="space-y-6">
      <EmailDisplay email={user?.email} />

      {/* Error Display */}
      {error && (
        <InlineAlert
          type="error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      <NameSection
        fullName={formData.fullName}
        editingName={editingName}
        loading={fieldsLoading}
        onEdit={() => setEditingName(true)}
        onSave={() => handleNameUpdate(formData.fullName)}
        onCancel={() => cancelEdit("name")}
        onChange={handleFormChange}
      />

      <PhoneSection
        phone={formData.phone}
        editingPhone={editingPhone}
        loading={fieldsLoading}
        error={errors.phone}
        onEdit={() => setEditingPhone(true)}
        onSave={() => handlePhoneUpdate(formData.phone)}
        onCancel={() => cancelEdit("phone")}
        onChange={handleFormChange}
      />

      <UsbcIdSection
        usbcId={formData.usbcId}
        editingUsbcId={editingUsbcId}
        loading={fieldsLoading}
        error={errors.usbcId}
        onEdit={() => setEditingUsbcId(true)}
        onSave={() => handleUsbcIdUpdate(formData.usbcId)}
        onCancel={() => cancelEdit("usbcId")}
        onChange={handleFormChange}
      />

      <PasswordSection
        editingPassword={editingPassword}
        loading={passwordLoading}
        errors={errors}
        formData={formData}
        passwordChecks={passwordChecks}
        showPassword={showPassword}
        showConfirmPassword={showConfirmPassword}
        hasPassword={hasPassword}
        requireOTP={requireOTP}
        sendingOTP={sendingOTP}
        otpError={otpError}
        userEmail={user?.email}
        onEdit={handlePasswordEdit}
        onSave={handlePasswordUpdate}
        onCancel={() => cancelEdit("password")}
        onChange={handleFormChange}
        onTogglePassword={() => setShowPassword(!showPassword)}
        onToggleConfirmPassword={() =>
          setShowConfirmPassword(!showConfirmPassword)
        }
        onSendOTP={sendOTP}
        onOTPCodeChange={handleOTPCodeChange}
        onResendOTP={resendOTP}
        otpCode={otpCode}
        otpCodeValid={otpCodeValid}
        otpCodeSent={otpCodeSent}
      />

      <SupportSection />
    </div>
  );
};

export default ProfileForm;
